import { Component, inject } from '@angular/core';
import { IonicModule, LoadingController, NavController, ToastController } from '@ionic/angular';
import { SharedModule } from '../../components/shared.module';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { RehearsalService } from './rehearsal.service';
import { Rehearsal } from './rehearsal';
import { RehearsalRequestDTO } from './RehearsalRequestDTO';
import { RehearsalScene } from './rehearsal-scene';
import { PerformanceService } from '../../services/performance.service';
import { AuthService } from '../auth/auth.service';

import { RehearsalRequest } from './rehearsal-request';

@Component({
    selector: 'app-external-rehearsal',
    templateUrl: './external-rehearsal.page.html',
    styleUrls: ['./external-rehearsal.page.scss'],
    imports: [
    IonicModule,
    SharedModule,
    FormsModule
]
})
export class ExternalRehearsalPage {

    protected token: string | null = null;
    protected rehearsal: Rehearsal | null = null;
    protected rehearsalRequest: RehearsalRequest = new RehearsalRequest();
    protected isEditMode: boolean = false;
    protected isSaving: boolean = false;
    protected hasBeenSaved: boolean = false;
    protected hideForm: boolean = false;
    protected items: RehearsalScene[] = [];
    protected isLoading: boolean = true;
    protected isUserAuthenticated: boolean = false;

    private route: ActivatedRoute = inject(ActivatedRoute);
    private rehearsalService: RehearsalService = inject(RehearsalService);
    private performanceService: PerformanceService = inject(PerformanceService);
    private authService: AuthService = inject(AuthService);
    private loadingController: LoadingController = inject(LoadingController);
    private toastController: ToastController = inject(ToastController);
    private readonly navController: NavController = inject(NavController);

    public async ionViewDidEnter(): Promise<void> {
        this.loadTokenFromRoute();
        try {
            this.isUserAuthenticated = this.authService.getUserIsAuthenticated().getValue();
            this.isLoading = true;

            if (this.isUserAuthenticated) {
                await this.loadForAuthenticatedUser();
            } else {
                await this.loadForExternalUser();
            }

            this.isEditMode = this.hasExistingData(this.rehearsal);
        } catch (error) {
            console.error('Error loading rehearsal:', error);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Load rehearsal data via the public token-based endpoint so
     * unauthenticated users never hit protected endpoints that would
     * trigger a redirect to the login screen.
     */
    private async loadForExternalUser(): Promise<void> {
        const data = await this.rehearsalService.getExternalRehearsalData(this.token);
        this.items = (data?.scenes ?? []).map(item => new RehearsalScene().create(item));
        this.items.sort((a, b) => a.orderIndex - b.orderIndex);

        this.rehearsal = new Rehearsal().create(data?.rehearsal);
        this.rehearsalRequest.performance = this.rehearsal.performance as unknown as typeof this.rehearsalRequest.performance;
    }

    /**
     * Authenticated users (admins/crew) may also land on this page — keep
     * using the existing authenticated endpoints so their access is logged
     * and scoped correctly.
     */
    private async loadForAuthenticatedUser(): Promise<void> {
        await this.loadRehearsalItems();
        const rehearsal = await this.rehearsalService.getByRehearsalToken(this.token);
        this.rehearsal = new Rehearsal().create(rehearsal);
        this.rehearsalRequest.performance = await this.performanceService.getById(this.rehearsal.performance.id);
        // Ensure rehearsal has performance datetime for summary
        this.rehearsal.performance.datetime = this.rehearsalRequest.performance?.datetime;
    }

    /**
     * Checks if the rehearsal has existing data that indicates edit mode
     */
    private hasExistingData(rehearsal: Rehearsal): boolean {
        if (!rehearsal) {
            return false;
        }
        const hasNotes = (rehearsal.notes?.trim().length ?? 0) > 0;
        const hasPurpose = (rehearsal.purpose?.trim().length ?? 0) > 0;
        const hasScenes = (rehearsal.scenes?.length ?? 0) > 0;
        const hasPersonName = (rehearsal.personName?.trim().length ?? 0) > 0;

        return hasNotes || hasPurpose || hasScenes || hasPersonName;
    }

    protected async onFormSave(dto: RehearsalRequestDTO): Promise<void> {
        const loader = this.loadingController.create({
            message: 'Bezig met opslaan...'
        });
        await (await loader).present();
        this.isSaving = true;

        if (this.token == null) {
            await this.showToast('Ongeldige token. Repetitie kon niet opgeslagen worden.', 'danger');
            this.isSaving = false;
            await (await loader).dismiss();
            return;
        }

        // Add performance ID and token to DTO
        dto.performanceId = this.rehearsalRequest.performance?.id || 0;
        dto.token = this.token;

        try {
            if (this.isEditMode) {
                await this.rehearsalService.updateRehearsalByToken(this.token, dto);
                await this.showToast('Repetitie bijgewerkt', 'success');
            } else {
                await this.rehearsalService.save(dto);
                await this.showToast('Repetitie opgeslagen', 'success');
            }

            if (this.isUserAuthenticated) {
                // Go to the dashboard rather than performance-edit: that route is
                // guarded by ShowFlagGuard {showFlag: 'hasPerformances'} against the
                // ACTIVE show, which bounces (with a confusing "functie niet
                // beschikbaar" alert) when the rehearsal's performance belongs to a
                // different show than the one currently active.
                await this.navController.navigateRoot(['/dashboard']);
            } else {
                this.hasBeenSaved = true;
                this.hideForm = true;
            }

        } catch (err) {
            console.error('saveRehearsal error', err);
            await this.showToast('Repetitie kon niet opgeslagen worden. Probeer het later opnieuw.', 'danger');

        } finally {
            this.isSaving = false;
            await (await loader).dismiss();
        }
    }

    private loadTokenFromRoute(): void {
        this.token = this.route.snapshot.paramMap.get('token');
    }

    private async loadRehearsalItems(): Promise<void> {
        this.items = await this.rehearsalService.getAll();
    }

    private async showToast(message: string, color: string): Promise<void> {
        const toast = await this.toastController.create({
            message,
            duration: 4000,
            color
        });
        await toast.present();
    }
}
